import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { DataTable } from "../DataTable/DataTable";
import { AdminOrderService } from "../../services/api/AdminOrderService/admin.order.service";

export function OrderHistory() {
  const [isLoading, setIsLoading] = useState(true);
  const [orderList, setOrderList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [medicineOrders, setMedicineOrders] = useState<any[]>([]);
  const [cancelledOrders, setCancelledOrders] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    all: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 10,
    },
    medicine: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 10,
    },
    cancelled: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 10,
    },
  });
  const [activeTab, setActiveTab] = useState<"all" | "medicine" | "cancelled">(
    "all"
  );
  const [withPrescription, setWithPrescription] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchOrders = async (searchText = "") => {
      try {
        setLoading(true);

        const currentTabPagination =
          pagination[activeTab as keyof typeof pagination];
        const currentPageToUse = currentTabPagination.currentPage;
        const pageSizeToUse = currentTabPagination.pageSize;

        if (!isMounted) return;

        if (activeTab === "all") {
          const allTabresponse = await AdminOrderService.getAdminOrdersListing(
            activeTab === "all" ? currentPageToUse : 1,
            pageSizeToUse,
            {
              searchText,
            }
          );

          const allorders = allTabresponse?.data?.data || [];
          const allTotalCount =
            allTabresponse?.data?.pagination?.totalCount || 0;
          const allTotalPages =
            allTabresponse?.data?.pagination?.totalPages || 1;
          setPagination((prev) => ({
            ...prev,
            all: {
              ...prev.all,
              totalCount: allTotalCount,
              totalPages: allTotalPages,
            },
          }));

          const transformedOrdersAll = allorders.map((order: any) => {
            // Better patient name handling
            const patientName =
              order.patientName ||
              order.patient?.name ||
              `Patient ${order.patientId?.slice(0, 6) || "XXXXXX"}`;

            const clinicInfo = order.clinicDetails
              ? {
                  name: order.clinicDetails.clinicName || "N/A",
                  supervisor: order.clinicDetails.clinicSupervisorName || "N/A",
                  address:
                    order.clinicDetails.clinicAddress ||
                    "Address not available",
                  contact: order.clinicDetails.clinicMobileNumber || "N/A",
                  image: order.clinicDetails.clinicProfileImage || null,
                }
              : {
                  name: "No clinic assigned",
                  supervisor: "N/A",
                  address: "N/A",
                  contact: "N/A",
                  image: null,
                };

            return {
              ...order,
              id: order.id,
              patientName,
              orderNumber: order.orderNumber || `ORD-${order.id}`,
              orderType: order.orderType || "UNKNOWN",
              date: new Date(order.orderDate).toLocaleDateString(),
              details: (order.productOrderItems || [])
                .map((item: any) => item.productName)
                .join(", "),
              productDetails: (order.productOrderItems || [])
                .map((item: any) => item.productName)
                .join(", "),
              orderBy: patientName,
              assignedToClinic: clinicInfo.name,
              clinicInfo,
              paymentMethod: order.paymentMethod || "Credit Card",
              hasPrescription: order.hasPrescription === true, // Explicit boolean conversion
              paymentStatus: order.paymentStatus || "Pending",
              status: (order.paymentStatus || "Pending").toLowerCase(),
            };
          });

          if (isMounted) {
            setOrderList(transformedOrdersAll);
            setIsLoading(false);
          }
        } else if (activeTab === "medicine") {
          const medicineTabresponse =
            await AdminOrderService.getAdminOrdersListing(
              activeTab === "medicine" ? currentPageToUse : 1,
              pageSizeToUse,
              {
                orderStatus: "COMPLETED",
                hasPrescription: true,
              }
            );

          const medicineorders = medicineTabresponse?.data?.data || [];
          const medicineTotalCount =
            medicineTabresponse?.data?.pagination?.totalCount || 0;
          const medicineTotalPages =
            medicineTabresponse?.data?.pagination?.totalPages || 1;

          setPagination((prev) => ({
            ...prev,
            medicine: {
              ...prev.medicine,
              totalCount: medicineTotalCount,
              totalPages: medicineTotalPages,
            },
          }));

          const medicineOrdersAll = medicineorders.map((order: any) => {
            // Better patient name handling
            const patientName =
              order.patientName ||
              order.patient?.name ||
              `Patient ${order.patientId?.slice(0, 6) || "XXXXXX"}`;

            const clinicInfo = order.clinicDetails
              ? {
                  name: order.clinicDetails.clinicName || "N/A",
                  supervisor: order.clinicDetails.clinicSupervisorName || "N/A",
                  address:
                    order.clinicDetails.clinicAddress ||
                    "Address not available",
                  contact: order.clinicDetails.clinicMobileNumber || "N/A",
                  image: order.clinicDetails.clinicProfileImage || null,
                }
              : {
                  name: "No clinic assigned",
                  supervisor: "N/A",
                  address: "N/A",
                  contact: "N/A",
                  image: null,
                };

            return {
              ...order,
              id: order.id,
              patientName,
              orderNumber: order.orderNumber || `ORD-${order.id}`,
              orderType: order.orderType || "UNKNOWN",
              date: new Date(order.orderDate).toLocaleDateString(),
              details: (order.productOrderItems || [])
                .map((item: any) => item.productName)
                .join(", "),
              productDetails: (order.productOrderItems || [])
                .map((item: any) => item.productName)
                .join(", "),
              orderBy: patientName,
              assignedToClinic: clinicInfo.name,
              clinicInfo,
              paymentMethod: order.paymentMethod || "Credit Card",
              hasPrescription: order.hasPrescription === true, // Explicit boolean conversion
              paymentStatus: order.paymentStatus || "Pending",
              status: (order.paymentStatus || "Pending").toLowerCase(),
            };
          });

          const medOrders = medicineOrdersAll.filter((order) =>
            order.orderType?.toUpperCase().includes("PRESCRIPTION")
          );

          if (isMounted) {
            setMedicineOrders(medOrders);
            setIsLoading(false);
          }
        } else {
          const cancelledTabresponse =
            await AdminOrderService.getAdminOrdersListing(
              activeTab === "cancelled" ? currentPageToUse : 1,
              pageSizeToUse,
              {
                orderStatus: "CANCELLED",
              }
            );

          const cancelledOrdersData = cancelledTabresponse?.data?.data || [];
          const cancelledTotalCount =
            cancelledTabresponse?.data?.pagination?.totalCount || 0;
          const cancelledTotalPages =
            cancelledTabresponse?.data?.pagination?.totalPages || 1;

          setPagination((prev) => ({
            ...prev,
            cancelled: {
              ...prev.cancelled,
              totalCount: cancelledTotalCount,
              totalPages: cancelledTotalPages,
            },
          }));

          const transformedOrdersCancelled = cancelledOrdersData.map(
            (order: any) => {
              // Better patient name handling
              const patientName =
                order.patientName ||
                order.patient?.name ||
                `Patient ${order.patientId?.slice(0, 6) || "XXXXXX"}`;

              const clinicInfo = order.clinicDetails
                ? {
                    name: order.clinicDetails.clinicName || "N/A",
                    supervisor:
                      order.clinicDetails.clinicSupervisorName || "N/A",
                    address:
                      order.clinicDetails.clinicAddress ||
                      "Address not available",
                    contact: order.clinicDetails.clinicMobileNumber || "N/A",
                    image: order.clinicDetails.clinicProfileImage || null,
                  }
                : {
                    name: "No clinic assigned",
                    supervisor: "N/A",
                    address: "N/A",
                    contact: "N/A",
                    image: null,
                  };

              return {
                ...order,
                id: order.id,
                patientName,
                orderNumber: order.orderNumber || `ORD-${order.id}`,
                orderType: order.orderType || "UNKNOWN",
                date: new Date(order.orderDate).toLocaleDateString(),
                details: (order.productOrderItems || [])
                  .map((item: any) => item.productName)
                  .join(", "),
                productDetails: (order.productOrderItems || [])
                  .map((item: any) => item.productName)
                  .join(", "),
                orderBy: patientName,
                assignedToClinic: clinicInfo.name,
                clinicInfo,
                paymentMethod: order.paymentMethod || "Credit Card",
                hasPrescription: order.hasPrescription === true, // Explicit boolean conversion
                paymentStatus: order.paymentStatus || "Pending",
                status: (order.paymentStatus || "Pending").toLowerCase(),
              };
            }
          );

          const cancelledOrders = transformedOrdersCancelled.filter((order) =>
            order.orderStatus?.toUpperCase().includes("CANCELLED")
          );

          if (isMounted) {
            setCancelledOrders(cancelledOrders);
            setIsLoading(false);
          }
        }
      } catch (err) {
        console.error("Fetch Error:", err);
        if (isMounted) {
          setIsLoading(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [
    pagination[activeTab as keyof typeof pagination].currentPage,
    pagination[activeTab as keyof typeof pagination].pageSize,
    withPrescription,
    activeTab,
    searchText,
  ]);

  const columns = [
    {
      key: "productDetails",
      title: "Product Details",
      width: "w-[200px]",
      render: (row: any) => (
        <div>
          <div className="text-sm font-medium">{row.productDetails}</div>
          <div className="text-xs text-gray-500">Order #{row.orderNumber}</div>
        </div>
      ),
    },
    {
      key: "orderBy",
      title: "Order By",
      width: "w-[200px]",
      render: (row: any) => (
        <div>
          <div className="text-sm font-medium">{row.patientName}</div>
          <div className="text-xs text-gray-500">{row.date}</div>
        </div>
      ),
    },
    {
      key: "assignedToClinic",
      title: "Assigned Clinic",
      width: "w-[250px]",
      render: (row: any) => (
        <div className="flex items-center">
          {row.clinicInfo.image && (
            <img
              src={row.clinicInfo.image}
              alt="Clinic"
              className="w-8 h-8 rounded-full mr-2 object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <div>
            <div className="text-sm font-medium">{row.clinicInfo.name}</div>
            <div className="text-xs text-gray-500">
              {row.clinicInfo.supervisor &&
                `Supervisor: ${row.clinicInfo.supervisor}`}
            </div>
            <div className="text-xs text-gray-500">
              {row.clinicInfo.contact && `Contact: ${row.clinicInfo.contact}`}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "paymentMethod",
      title: "Payment Method",
      width: "w-[200px]",
      render: (row: any) => (
        <div>
          <div className="text-sm font-medium">{row.paymentMethod}</div>
        </div>
      ),
    },
    {
      key: "status",
      title: "Status",
      width: "w-[200px]",
      render: (row: any) => {
        const orderStatus = row.orderStatus || "UNKNOWN";

        // Define all possible status configurations
        const statusConfigs = {
          all: {
            COMPLETED: {
              text: "Completed",
              class: "bg-green-100 text-green-800",
            },
            CANCELLED: { text: "Cancelled", class: "bg-red-100 text-red-800" },
            PENDING_ADMIN: {
              text: "Pending Admin",
              class: "bg-yellow-100 text-yellow-800",
            },
            PENDING_PATIENT: {
              text: "Pending Patient",
              class: "bg-yellow-100 text-yellow-800",
            },
            CLINIC_ASSIGNED: {
              text: "Clinic Assigned",
              class: "bg-blue-100 text-blue-800",
            },
            DELIVERY_ASSIGNED: {
              text: "Delivery Assigned",
              class: "bg-blue-100 text-blue-800",
            },
            OUT_FOR_PICKUP: {
              text: "Out For Pickup",
              class: "bg-blue-100 text-purple-500",
            },
            OUT_FOR_DELIVERY: {
              text: "Out for Delivery",
              class: "bg-purple-100 text-purple-800",
            },
            PICKUP_ASSIGNED: {
              text: "Pickup Assigned",
              class: "bg-blue-100 text-blue-800",
            },
            PICKUP_RECIEVED: {
              text: "Pickup Received",
              class: "bg-purple-100 text-purple-800",
            },
            CLINIC_RECIEVED: {
              text: "Clinic Received",
              class: "bg-purple-100 text-purple-800",
            },
            FORWARDED_TO_LAB: {
              text: "Forwarded to Lab",
              class: "bg-blue-100 text-blue-800",
            },
            REPORT_GENERATED: {
              text: "Report Generated",
              class: "bg-green-100 text-green-800",
            },
            RETURN: {
              text: "Returned",
              class: "bg-orange-100 text-orange-800",
            },
            UNKNOWN: { text: "Unknown", class: "bg-gray-100 text-gray-800" },
          },
          medicine: {
            COMPLETED: {
              text: "Completed",
              class: "bg-green-100 text-green-800",
            },
            UNKNOWN: { text: "Unknown", class: "bg-gray-100 text-gray-800" },
          },
          cancelled: {
            CANCELLED: { text: "Cancelled", class: "bg-red-100 text-red-800" },
            UNKNOWN: { text: "Unknown", class: "bg-gray-100 text-gray-800" },
          },
        };

        // Select the appropriate config based on activeTab
        const currentConfig = statusConfigs[activeTab] || statusConfigs.all;

        // Get the status configuration or fallback to UNKNOWN
        const { text, class: statusClass } =
          currentConfig[orderStatus] || currentConfig.UNKNOWN;

        return (
          <div>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}
            >
              {text}
            </span>
          </div>
        );
      },
    },
  ];

  const handleTabChange = (tab: "all" | "medicine" | "cancelled") => {
    setActiveTab(tab);
    setWithPrescription(tab === "medicine");
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab as keyof typeof pagination],
        currentPage: newPage,
      },
    }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setPagination((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab as keyof typeof pagination],
        pageSize: newSize,
        currentPage: 1,
      },
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex border-b">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "all"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => handleTabChange("all")}
          >
            All Orders
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "medicine"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => handleTabChange("medicine")}
          >
            Medicine Orders
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "cancelled"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => handleTabChange("cancelled")}
          >
            Cancelled Orders
          </button>
        </div>

        <div className="mb-4">
          <Search className="relative top-[2.25rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchText}
            onChange={handleSearchChange}
            className="border rounded-lg px-3 py-2.5 text-sm w-64 text-center"
          />
        </div>
      </div>

      <DataTable
        data={
          activeTab === "all"
            ? orderList
            : activeTab === "medicine"
            ? medicineOrders
            : activeTab === "cancelled"
            ? cancelledOrders
            : []
        }
        columns={columns}
        itemsPerPage={pagination[activeTab as keyof typeof pagination].pageSize}
        isLoading={isLoading || loading}
        pagination={{
          currentPage:
            pagination[activeTab as keyof typeof pagination].currentPage,
          setCurrentPage: handlePageChange,
          totalPages:
            pagination[activeTab as keyof typeof pagination].totalPages,
          pageSize: pagination[activeTab as keyof typeof pagination].pageSize,
          setPageSize: handlePageSizeChange,
          totalCount:
            pagination[activeTab as keyof typeof pagination].totalCount,
        }}
      />
    </div>
  );
}
